import { Play, Heart, ThumbsUp } from 'lucide-react';
import { Companion } from '../api/model';

interface CompanionCardProps {
  companion: Companion;
  onStartChat: (companion: Companion) => void;
  onCompanionSelect: (companion: Companion) => void;
  isFeatured: boolean;
}

function CompanionCard({
  companion,
  onStartChat,
  onCompanionSelect,
  isFeatured,
}: CompanionCardProps) {
  return (
    <div
      className="group relative flex-shrink-0 w-72 lg:w-96 cursor-pointer"
      onClick={() => onCompanionSelect(companion)}
    >
      <div
        className={`relative overflow-hidden rounded-2xl bg-kind-container border transition-all duration-300 hover:shadow-lg hover:bg-kind-container/80 ${
          isFeatured
            ? 'border-kind-purple shadow-lg shadow-kind-purple/20'
            : 'border-gray-300 dark:border-white/10'
        }`}
      >
        <div
          style={{
            backgroundImage: `url(${companion.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
          className="w-full h-56 lg:h-72 object-cover transition-all duration-500 group-hover:scale-110"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
          {isFeatured && (
            <div className="absolute top-4 right-4">
              <div className="bg-kind-purple text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                Featured
              </div>
            </div>
          )}
        </div>

        {/* Info Section - now part of the same card element */}
        <div className="p-4 lg:p-6 bg-kind-container/95 backdrop-blur-sm relative">
          {/* Name */}
          <div className="absolute -top-16">
            <h3 className="text-lg lg:text-2xl font-title font-medium text-white mb-2 lg:mb-3 leading-tight">
              {companion.name}
            </h3>
          </div>
          {/* Chat Button */}
          <div className="flex items-center justify-between mb-3 lg:mb-4 absolute -top-32 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStartChat(companion);
              }}
              className="flex items-center space-x-2 bg-kind-purple text-white px-4 lg:px-6 py-2 lg:py-3 rounded-2xl text-xs lg:text-sm font-bold hover:bg-kind-purple/90 transition-colors duration-200"
            >
              <Play className="w-3 h-3 lg:w-4 lg:h-4 fill-current" />
              <span>Chat</span>
            </button>
          </div>
          <p className="text-sm lg:text-base font-body font-light text-kind-text-secondary mb-3 lg:mb-4 line-clamp-2 leading-relaxed h-10 lg:h-12">
            {companion.description}
          </p>

          <div className="flex items-center justify-between text-xs lg:text-sm">
            <div className="flex items-center space-x-2 lg:space-x-3">
              <span
                className={`px-2 lg:px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                  companion.category === 'Career'
                    ? 'bg-kind-purple text-white'
                    : companion.category === 'Relationships'
                    ? 'bg-kind-teal text-white'
                    : companion.category === 'Health'
                    ? 'bg-kind-blue text-white'
                    : companion.category === 'Personal Growth'
                    ? 'bg-kind-purple text-white'
                    : 'bg-kind-purple text-white'
                }`}
              >
                {companion.category}
              </span>
              <div className="flex items-center space-x-1">
                <ThumbsUp className="w-3 h-3 text-kind-purple" />
                <span className="text-kind-text-primary font-medium">{companion.rating}</span>
              </div>
            </div>
            <span className="text-kind-purple font-medium">{companion.chat_count} chats</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanionCard;
